import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import GroupsIcon from "@mui/icons-material/Groups";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";

const SEATS_MIN = 2;
const SEATS_MAX = 22;

// Mirrors SPLIT_SEATS_BY_SPORT in the API's OpenGame/constants.js, keyed by the
// sport's icon slug. A padel court holds four players, so offering 22 makes the
// picker look broken; fewer than max is always allowed. Sports absent here fall
// back to the permissive global range, and the server re-checks the same cap.
const SEATS_BY_SPORT = {
  padel: { preset: 4, max: 4 },
  tennis: { preset: 2, max: 4 },
  squash: { preset: 2, max: 4 },
  badminton: { preset: 4, max: 4 },
  table_tennis: { preset: 2, max: 4 },
  basketball: { preset: 10, max: 10 },
  volleyball: { preset: 12, max: 12 },
  football: { preset: 10, max: 22 }
};

export const getSeatLimits = (sportIcon) => {
  const found = SEATS_BY_SPORT[String(sportIcon || "").toLowerCase()];
  const { preset, max } = found || { preset: 4, max: SEATS_MAX };
  return { min: SEATS_MIN, max, preset };
};

// Mirrors the server's deadline-capture inputs (OpenGame/constants.js) for the
// checkout ESTIMATE only — the authoritative captureDueAt is computed when the
// hold settles and shown on the hold/game pages. Capture fires at booking
// start (the hold stays alive until the game, keeping host cancel + friends'
// free leave + desk settlement possible); the hold horizon caps how far ahead
// a split can be offered at all.
const HOLD_VALIDITY_DAYS = 5;
const CAPTURE_SAFETY_HOURS = 12;
const MAX_LEAD_HOURS = HOLD_VALIDITY_DAYS * 24 - CAPTURE_SAFETY_HOURS;

/**
 * "Split the cost" panel on the court checkout. The host authorizes a card
 * HOLD for the full amount; friends claim seats through the booking link and
 * pay their own shares; unpaid spots are charged to the host's card when the
 * game starts (or at hold expiry − safety, whichever comes first). Bookings
 * starting beyond the hold horizon can't be split — the panel renders
 * disabled with an explanation (the server enforces the same cap).
 */
function CheckoutSplitPanel({ enabled, onToggle, seats, onSeatsChange, totalAmount, currency, item, seatLimits }) {
  const { min: seatsMin, max: seatsMax } = seatLimits || { min: SEATS_MIN, max: SEATS_MAX };
  const share = seats > 0 ? Math.floor((totalAmount / seats) * 100) / 100 : 0;

  const { deadline, capturesToday, tooFarAhead } = useMemo(() => {
    const now = DateTime.local();
    const candidates = [now.plus({ days: HOLD_VALIDITY_DAYS }).minus({ hours: CAPTURE_SAFETY_HOURS })];

    // Booking start in the viewer's local clock — club wall time is close
    // enough for a checkout estimate.
    const start = item?.date && item?.time ? DateTime.fromISO(`${item.date}T${item.time}`) : null;
    if (start?.isValid) candidates.push(start);

    const future = candidates.filter((d) => d.isValid && d > now);
    const due = future.length ? DateTime.min(...future) : now;
    return {
      deadline: due,
      // A game starting today is captured today, so the usual "no charge today"
      // opener would be a lie — the copy switches to "now" wording instead.
      capturesToday: due.hasSame(now, "day"),
      tooFarAhead: !!start?.isValid && start > now.plus({ hours: MAX_LEAD_HOURS })
    };
  }, [item?.date, item?.time]);

  if (tooFarAhead) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <GroupsIcon sx={{ color: "text.disabled" }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2, color: "text.disabled" }}>
              Split the cost with friends
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Available for games starting within {Math.floor(MAX_LEAD_HOURS / 24)} days — this booking is too far ahead for the card
              hold that secures the court.
            </Typography>
          </Box>
          <Switch checked={false} disabled />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 2.5 },
        ...(enabled && { borderColor: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.03) })
      }}
    >
      {/* Icon pinned to the title line rather than centred on the whole block:
          the caption wraps to two lines on a phone, which dragged the icon and
          the switch out of line with the heading they belong to. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <GroupsIcon sx={{ color: enabled ? "primary.main" : "text.disabled", flexShrink: 0, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.3 }}>Split the cost with friends</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4, mt: 0.25 }}>
            Everyone pays their own share through your booking link
          </Typography>
        </Box>
        <Switch checked={enabled} onChange={(e) => onToggle(e.target.checked)} sx={{ flexShrink: 0, mt: -0.5, mr: -1 }} />
      </Box>

      {enabled && (
        <Stack spacing={2} sx={{ mt: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="body2" fontWeight={600}>
              Players (including you)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => onSeatsChange(Math.max(seatsMin, seats - 1))}
                disabled={seats <= seatsMin}
                sx={{ border: 1, borderColor: "divider" }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ width: "2rem", textAlign: "center", fontWeight: 800, fontSize: { xs: "1rem", md: "1.1rem" } }}>{seats}</Typography>
              <IconButton
                size="small"
                onClick={() => onSeatsChange(Math.min(seatsMax, seats + 1))}
                disabled={seats >= seatsMax}
                sx={{ border: 1, borderColor: "divider" }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              Each player pays
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: "1rem", md: "1.05rem" } }}>
              {currency}
              {share.toFixed(2)}
            </Typography>
          </Box>

          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.5 }}>
              {capturesToday ? "We place a hold of " : "No charge today — we place a hold of "}
              {currency}
              {totalAmount.toFixed(2)} on your card to secure the court. Friends pay their shares through your booking link.{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                Unpaid spots will be charged to your card{" "}
                {capturesToday ? `today at ${deadline.toFormat("h:mm a")}` : `on ${deadline.toFormat("ccc, MMM d 'at' h:mm a")}`}.
              </Box>
            </Typography>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}

export default CheckoutSplitPanel;
